import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import api from '../../services/api';

const SharedContentPage = () => {
  const { token } = useParams();
  const location = useLocation();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine content type from URL path
  const getContentType = () => {
    if (location.pathname.startsWith('/wishlist/')) return 'wishlist';
    if (location.pathname.startsWith('/occasion/')) return 'occasion';
    return 'unknown';
  };

  const contentType = getContentType();

  useEffect(() => {
    const fetchSharedContent = async () => {
      try {
        setLoading(true);
        setError(null);

        // Call the universal share endpoint
        const response = await api.get(`/${contentType}/${token}/`);
        
        if (response.data.type === 'share') {
          setContent(response.data.data);
        } else {
          // Handle direct content response
          setContent(response.data);
        }
        
      } catch (err) {
        console.error('Error fetching shared content:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load shared content');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchSharedContent();
    }
  }, [token, contentType]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-accent-600">Loading shared content...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-accent-800 mb-2">Content Not Available</h2>
              <p className="text-accent-600 mb-6">{error}</p>
              <Button
                variant="primary"
                onClick={() => window.history.back()}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Go Back
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No content data
  if (!content) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Icon name="Package" size={48} className="text-accent-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-accent-800 mb-2">No Content Found</h2>
              <p className="text-accent-600">Unable to load shared content.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getContentIcon = () => {
    switch (contentType) {
      case 'wishlist': return 'Heart';
      case 'occasion': return 'Calendar';
      default: return 'Share2';
    }
  };

  const getContentTitle = () => {
    switch (contentType) {
      case 'wishlist': return 'Shared Wishlist';
      case 'occasion': return 'Shared Occasion';
      default: return 'Shared Content';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{content.title || getContentTitle()} - Giftsy</title>
        <meta name="description" content={content.description || `View this ${contentType} shared on Giftsy`} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={content.title || getContentTitle()} />
        <meta property="og:description" content={content.description || `View this ${contentType} shared on Giftsy`} />
        {content.image_url && <meta property="og:image" content={content.image_url} />}
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Giftsy" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={content.title || getContentTitle()} />
        <meta name="twitter:description" content={content.description || `View this ${contentType} shared on Giftsy`} />
        {content.image_url && <meta name="twitter:image" content={content.image_url} />}
      </Helmet>

      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <Icon name={getContentIcon()} size={32} className="text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-accent-800 mb-2">
            {content.title || getContentTitle()}
          </h1>
          {content.description && (
            <p className="text-lg text-accent-600 max-w-2xl mx-auto">
              {content.description}
            </p>
          )}
          {content.shared_by && (
            <p className="text-sm text-accent-500 mt-2">
              Shared by {content.shared_by}
            </p>
          )}
        </div>

        {/* Content Section */}
        <div className="bg-white rounded-2xl border border-accent-100 p-6 mb-8">
          {content.image_url && (
            <div className="mb-6">
              <img
                src={content.image_url}
                alt={content.title}
                className="w-full h-64 object-cover rounded-xl"
              />
            </div>
          )}
          
          {/* Content Details */}
          <div className="space-y-4">
            {content.items && content.items.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-accent-800 mb-3">Items</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {content.items.map((item, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-accent-50 rounded-lg">
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                      )}
                      <div className="flex-1">
                        <h4 className="font-medium text-accent-800">{item.name}</h4>
                        {item.price && (
                          <p className="text-sm text-accent-600">{item.price}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {content.details && (
              <div>
                <h3 className="text-lg font-semibold text-accent-800 mb-3">Details</h3>
                <div className="prose prose-sm max-w-none">
                  <p className="text-accent-700">{content.details}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            variant="primary"
            iconName="Heart"
            iconPosition="left"
            onClick={() => console.log('Add to wishlist')}
          >
            Add to My Wishlist
          </Button>
          <Button
            variant="outline"
            iconName="Share2"
            iconPosition="left"
            onClick={() => console.log('Share this content')}
          >
            Share This
          </Button>
        </div>
      </main>
    </div>
  );
};

export default SharedContentPage;